
import { safeNavigate, safeReload } from '@/utils/navigation';

/**
 * Utility functions for handling authentication in PWA context
 */

/**
 * Check if Supabase session exists in storage
 */
const hasSupabaseSession = (): boolean => {
  try {
    // Check for Supabase auth token in localStorage
    const supabaseAuth = localStorage.getItem('sb-brmhxrfcrxzrtsxmgkep-auth-token');
    if (supabaseAuth) {
      const authData = JSON.parse(supabaseAuth);
      return !!(authData?.access_token && authData?.refresh_token);
    }
    
    // Fallback: check for legacy storage keys
    const legacyToken = localStorage.getItem('supabase.auth.token');
    return !!legacyToken;
  } catch (error) {
    console.warn('Error checking Supabase session:', error);
    return false;
  }
};

/**
 * Check if app is running in PWA standalone mode
 */
const isPWAStandalone = (): boolean => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://') ||
    localStorage.getItem('pwaStandalone') === 'true'
  );
};

/**
 * Listens for service worker auth messages with improved PWA handling
 */
export const setupServiceWorkerAuthListener = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('Service worker message received:', event.data);
      
      // If the service worker asks to check auth
      if (event.data && event.data.type === 'CHECK_AUTH') {
        const hasSession = hasSupabaseSession();
        const isStandalone = isPWAStandalone();
        
        console.log('Auth check - Has session:', hasSession, 'Is PWA:', isStandalone);
        
        if (hasSession) {
          const alreadyOnDashboard = window.location.pathname === '/dashboard';
          const redirected = sessionStorage.getItem('pwa_auth_redirected') === '1';
          
          if (!alreadyOnDashboard && !redirected) {
            sessionStorage.setItem('pwa_auth_redirected', '1');
            // Use replace to avoid back-button loops without full reload
            safeNavigate('/dashboard', true);
          }
        }
      }
      
      // Handle PWA session restoration requests
      if (event.data && event.data.type === 'RESTORE_SESSION') {
        const hasSession = hasSupabaseSession();
        console.log('Session restoration requested, has session:', hasSession);
        
        if (hasSession) {
          const alreadyReloaded = sessionStorage.getItem('pwa_session_reloaded') === '1';
          if (!alreadyReloaded) {
            sessionStorage.setItem('pwa_session_reloaded', '1');
            // Trigger a page refresh to restore session once per app session
            safeReload('pwa-restore-session');
          } else {
            console.log('PWA session already reloaded once; skipping reload');
          }
        }
      }
    });
    
    // Send initial auth state to service worker for PWA launches
    if (isPWAStandalone()) {
      navigator.serviceWorker.ready.then(registration => {
        const hasSession = hasSupabaseSession();
        console.log('PWA launched, sending auth state to SW:', hasSession);
        
        registration.active?.postMessage({
          type: 'PWA_AUTH_STATE',
          authenticated: hasSession,
          timestamp: Date.now()
        });
      });
    }
  }
};

/**
 * Setup PWA-specific session restoration
 */
export const setupPWASessionRestoration = () => {
  if (!isPWAStandalone()) return;
  
  console.log('Setting up PWA session restoration');
  
  // Check if we need to restore session on PWA launch
  const sessionCheckInterval = setInterval(() => {
    const hasSession = hasSupabaseSession();
    const currentPath = window.location.pathname;
    
    console.log('PWA session check - Has session:', hasSession, 'Current path:', currentPath);
    
    if (hasSession && currentPath === '/auth') {
      console.log('PWA: Redirecting authenticated user away from auth page');
      clearInterval(sessionCheckInterval);
      safeNavigate('/dashboard', true);
    }
    
    // Stop checking after 5 seconds
    setTimeout(() => clearInterval(sessionCheckInterval), 5000);
  }, 500);
};
